import { useState, useRef, useEffect, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { parseTransactionsFromText, aggregateByMonth, generateLocalAnalysis, EXPENSE_KEYS_LOCAL } from "./localEngine";

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

const ANTHROPIC_URL = process.env.NODE_ENV === "production"
  ? "/api/claude"
  : "http://localhost:3001/api/claude";
const MODEL = "claude-sonnet-4-6";

const LABELS = {
  income:        "Revenu mensuel net",
  rent:          "Loyer / Hypothèque",
  food:          "Alimentation",
  transport:     "Transport",
  utilities:     "Factures & Charges",
  subscriptions: "Abonnements",
  health:        "Santé",
  leisure:       "Loisirs & Sorties",
  savings:       "Épargne mensuelle",
  other:         "Autres dépenses",
};

const EXPENSE_KEYS = ["rent","food","transport","utilities","subscriptions","health","leisure","savings","other"];
const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const CHART_COLORS = ["#4ade80","#22d3ee","#f59e0b","#f472b6","#a78bfa","#fb923c","#34d399","#60a5fa","#e879f9"];

const SYSTEM_PROMPT = `Tu es BudgetAI, un conseiller financier et fiscal personnel expert français. Tu analyses des relevés bancaires et documents financiers pour: extraire et catégoriser toutes les transactions mois par mois, calculer la capacité d'emprunt, identifier les optimisations fiscales selon le profil, détecter les éventuelles erreurs d'imposition, et proposer des investissements personnalisés. Réponds TOUJOURS en français, de façon précise, chiffrée et personnalisée. Les graphiques sont affichés dans l'interface — ne les reproduis pas. IMPORTANT: n'utilise JAMAIS de tableaux markdown (pas de caractère |). Utilise uniquement des titres ##, des listes à puces avec - et du texte en gras **like this**.`;

const ANALYSIS_PROMPT = (summary, fileNames, historyStr, profile) => `[BUDGETAI — ANALYSE FINANCIÈRE COMPLÈTE]

PROFIL UTILISATEUR:
- Situation familiale: ${profile.situation}
- Enfants à charge: ${profile.nb_enfants}
- Statut professionnel: ${profile.statut_pro}
- Résidence: ${profile.proprietaire}

${summary && summary !== "(aucune saisie — extraire depuis les documents)" ? `Données saisies manuellement:\n${summary}\n` : ""}
${fileNames ? `Documents fournis: ${fileNames}\n` : ""}
${historyStr ? `Historique connu: ${historyStr}\n` : ""}

═══════════════════════════════════════════
ÉTAPE 1 — EXTRACTION JSON (OBLIGATOIRE, PRIORITÉ ABSOLUE)
═══════════════════════════════════════════
Analyse CHAQUE relevé et extrais les données pour CHAQUE mois distinct trouvé. Catégorise toutes les transactions. Commence IMMÉDIATEMENT par ce bloc JSON sans texte avant:

\`\`\`json
{
  "months": [
    {"month":"AAAA-MM","label":"Mmm AAAA","income":0,"rent":0,"food":0,"transport":0,"utilities":0,"subscriptions":0,"health":0,"leisure":0,"savings":0,"other":0}
  ],
  "borrowing_capacity": {"existing_loan_payments":0,"max_debt_ratio_payment":0,"remaining_capacity":0,"max_new_loan_amount":0,"rate_pct":3.5,"duration_years":20},
  "missing_documents": [],
  "tax_estimate": {"revenu_annuel_estime":0,"impot_estime":0,"taux_effectif_pct":0,"nb_parts":1.0,"alerte_erreur":false}
}
\`\`\`

Règles de catégorisation des transactions:
- income: virements salaire, primes, revenus freelance, APL, allocations, pensions reçues
- rent: loyer, charges copropriété, mensualité prêt immobilier
- food: supermarchés (Carrefour, Leclerc, Lidl, Monoprix...), restaurants, livraison repas, boulangerie
- transport: carburant, péages, SNCF, RATP, Uber, parking, assurance auto, leasing
- utilities: EDF, eau, téléphone, internet, assurance habitation, mutuelle
- subscriptions: Netflix, Spotify, Amazon Prime, salle de sport, logiciels SaaS
- health: pharmacie, médecin, dentiste, opticien (hors mutuelle)
- leisure: sorties, voyages, shopping vêtements, cadeaux, culture, jeux
- savings: virements vers Livret A, assurance-vie, PEA, compte épargne
- other: tout le reste, virements entre comptes propres, retraits DAB non identifiés

Calcul capacité d'emprunt (règle française des 35% — HCSF 2021):
1. Identifier TOUS les remboursements de crédits en cours détectés dans les relevés : prêt immobilier, crédit auto, crédit conso, leasing, prêt personnel, LOA. Sommer leur montant mensuel moyen → existing_loan_payments.
2. max_debt_ratio_payment = revenu_mensuel_moyen × 0.35 (plafond légal total d'endettement).
3. remaining_capacity = max_debt_ratio_payment − existing_loan_payments (ce qu'il reste pour un NOUVEL emprunt).
4. Si remaining_capacity ≤ 0 : le profil est déjà au plafond d'endettement, indiquer 0.
5. max_new_loan_amount = remaining_capacity × 171 (taux 3.5%, 20 ans).
Calcul impôt 2024: applique le barème progressif au revenu annuel estimé avec quotient familial (${profile.situation === "marié" || profile.situation === "pacsé" ? "2 parts base" : "1 part base"}, +0.5 par enfant jusqu'au 2e, +1 à partir du 3e).
Documents manquants: si aucun bulletin de salaire visible → "Bulletin de salaire". Si pas d'avis d'imposition → "Avis d'imposition N-1". Si indépendant sans bilan → "Liasse fiscale / Bilan comptable".

═══════════════════════════════════════════
ÉTAPE 2 — ANALYSE COMPLÈTE
═══════════════════════════════════════════

## 📊 Score de santé financière
Score /100 basé sur les ratios moyens de la période analysée. Cite les mois couverts.

## 📐 Analyse des ratios clés (moyenne sur la période)
Pour chaque poste de dépense, indique : montant actuel en €, pourcentage du revenu, écart vs standard, statut (✅ OK / ⚠️ Élevé / 🔴 Critique).
Standards cibles : Logement ≤33%, Alimentation 10-15%, Transport ≤10%, Loisirs ≤10%, Épargne ≥20%, Charges ≤10%, Abonnements ≤5%.

## ✂️ Plan de réduction des dépenses
Pour chaque poste AU-DESSUS du standard, fournis un plan de réduction concret et chiffré :
- Montant actuel → montant cible → économie mensuelle en €
- 2-3 actions concrètes et spécifiques pour réduire ce poste (ex: résilier X, remplacer Y par Z, négocier W)
- Délai réaliste pour mettre en place chaque action
Si tous les postes sont dans les normes, indique-le et propose des optimisations supplémentaires pour augmenter l'épargne.
À la fin, calcule le **gain mensuel total** si toutes les réductions sont appliquées et son impact sur le taux d'épargne.

## 🏦 Capacité d'emprunt résiduelle
Affiche le calcul complet :
- Plafond légal d'endettement (35% du revenu) : X €/mois
- Mensualités existantes détectées (prêts en cours) : − X €/mois → liste-les
- **Capacité résiduelle pour un nouvel emprunt : X €/mois**
- Montant empruntable supplémentaire estimé à 3,5% sur 20 ans : X €
- Type de bien accessible avec ce budget résiduel
- Si déjà au plafond : le dire clairement et expliquer comment améliorer le ratio (rembourser tel crédit en priorité)

## 💰 Optimisation fiscale & vérification
Dispositifs applicables au profil (PER, LDDS, PEA, dons, déficit foncier, CESU...).
Économie fiscale estimée en €. Alerte si écart détecté entre impôt estimé et ce qui semble avoir été prélevé.

## 📈 Stratégie d'investissement personnalisée
Répartition conseillée du solde mensuel disponible:
- Court terme (liquidité d'urgence)
- Moyen terme (3-5 ans)
- Long terme (>5 ans, retraite)

## 💡 Points forts & alertes
2-3 points forts et 2-3 alertes avec chiffres précis issus des relevés.

## 🎯 Actions prioritaires cette semaine
3 actions concrètes et chiffrées, dans l'ordre d'impact décroissant.`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

const fmt = (n) => Number(n).toLocaleString("fr-FR");

function parseMarkdown(text) {
  if (!text) return [];
  const lines = text.split("\n");
  const items = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Detect markdown table: row starting with |
    if (line.trim().startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      items.push({ type: "table", lines: tableLines, i: items.length });
      continue;
    }
    if (line.startsWith("## ")) items.push({ type: "h2", text: line.slice(3), i: items.length });
    else if (line.startsWith("### ")) items.push({ type: "h3", text: line.slice(4), i: items.length });
    else if (line.match(/^[-•]\s/)) items.push({ type: "li", text: line.slice(2), i: items.length });
    else if (line.startsWith("> ")) items.push({ type: "quote", text: line.slice(2), i: items.length });
    else if (line.trim() === "") items.push({ type: "br", i: items.length });
    else items.push({ type: "p", text: line, i: items.length });
    i++;
  }
  return items;
}

function MDLine({ item }) {
  const bold = (t) =>
    t.split(/\*\*(.+?)\*\*/g).map((s, j) =>
      j % 2 === 1 ? <strong key={j} style={{ color: "#e8eaed" }}>{s}</strong> : s
    );

  if (item.type === "h2") return (
    <h2 style={{ color: "#4ade80", fontSize: 15, fontWeight: 700, margin: "18px 0 8px", borderBottom: "1px solid rgba(74,222,128,0.2)", paddingBottom: 5 }}>
      {item.text}
    </h2>
  );
  if (item.type === "h3") return (
    <h3 style={{ color: "#22d3ee", fontSize: 13, fontWeight: 600, margin: "12px 0 4px" }}>{item.text}</h3>
  );
  if (item.type === "li") return (
    <div style={{ display: "flex", gap: 8, margin: "4px 0 4px 4px" }}>
      <span style={{ color: "#4ade80", flexShrink: 0, marginTop: 2 }}>▸</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.6 }}>{bold(item.text)}</span>
    </div>
  );
  if (item.type === "quote") return (
    <div style={{ borderLeft: "3px solid rgba(74,222,128,0.4)", paddingLeft: 12, margin: "6px 0", color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>
      {bold(item.text)}
    </div>
  );
  if (item.type === "table") {
    const rows = item.lines
      .filter(l => !l.match(/^\|[-| :]+\|$/))
      .map(l => l.split("|").filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim()));
    if (!rows.length) return null;
    const [header, ...body] = rows;
    return (
      <div style={{ overflowX: "auto", margin: "10px 0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr>
              {header.map((cell, j) => (
                <th key={j} style={{ padding: "6px 10px", textAlign: "left", color: "#4ade80", borderBottom: "1px solid rgba(74,222,128,0.3)", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {bold(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                {row.map((cell, ci) => (
                  <td key={ci} style={{ padding: "6px 10px", color: "#d1d5db" }}>{bold(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (item.type === "br") return <div style={{ height: 6 }} />;
  return <p style={{ fontSize: 13.5, margin: "3px 0", lineHeight: 1.6 }}>{bold(item.text)}</p>;
}

function MarkdownRenderer({ text }) {
  return <div>{parseMarkdown(text).map((item) => <MDLine key={item.i} item={item} />)}</div>;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function BudgetAI() {
  const [screen, setScreen] = useState("home");
  const [form, setForm] = useState(
    Object.fromEntries(Object.keys(LABELS).map((k) => [k, ""]))
  );
  const [profile, setProfile] = useState({ situation: "célibataire", nb_enfants: "0", statut_pro: "salarié", proprietaire: "locataire" });
  const [files, setFiles] = useState([]);
  const [fileContents, setFileContents] = useState([]);
  const [extractedMetrics, setExtractedMetrics] = useState(null);
  const [borrowingData, setBorrowingData] = useState(null);
  const [missingDocs, setMissingDocs] = useState([]);
  const [taxEstimate, setTaxEstimate] = useState(null);
  const [newlyAddedByDoc, setNewlyAddedByDoc] = useState({});
  const [localMode, setLocalMode] = useState(false);
  const [customDocLabel, setCustomDocLabel] = useState("");
  const [customDocs, setCustomDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [monthlyHistory, setMonthlyHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("budgetai_history") || "[]"); }
    catch { return []; }
  });

  const fileRef = useRef();
  const chatEndRef = useRef();
  const pendingDocRef = useRef(null); // tracks which doc type triggered the file picker

  // ── Derived data ──
  const expenseData = useCallback(() => {
    const income = parseFloat(form.income) || extractedMetrics?.income || 0;
    const items = EXPENSE_KEYS
      .map((k) => ({ key: k, label: LABELS[k], value: parseFloat(form[k]) || extractedMetrics?.[k] || 0 }))
      .filter((x) => x.value > 0);
    const total = items.reduce((s, x) => s + x.value, 0);
    return { income, items, total, balance: income - total };
  }, [form, extractedMetrics]);

  const savingsRate = () => {
    const ed = expenseData();
    return ed.income > 0 ? Math.round((ed.balance / ed.income) * 100) : 0;
  };

  const hasSomeData = Object.values(form).some((v) => v && v.trim() !== "") || files.length > 0;

  // ── File handling ──
  const handleFiles = async (fileList) => {
    const forDoc = pendingDocRef.current;
    pendingDocRef.current = null;

    const newFiles = Array.from(fileList).filter(
      (f) => !files.find((x) => x.name === f.name)
    );
    if (!newFiles.length) return;

    const newContents = await Promise.all(
      newFiles.map(async (f) => ({ name: f.name, type: f.type, b64: await toBase64(f) }))
    );
    setFiles((p) => [...p, ...newFiles]);
    setFileContents((p) => [...p, ...newContents]);
    if (fileRef.current) fileRef.current.value = "";

    if (forDoc) {
      setNewlyAddedByDoc((p) => ({ ...p, [forDoc]: (p[forDoc] || 0) + newFiles.length }));
    }
  };

  const addDocForType = (docName) => {
    pendingDocRef.current = docName;
    fileRef.current?.click();
  };

  const removeFile = (i) => {
    setFiles((p) => p.filter((_, j) => j !== i));
    setFileContents((p) => p.filter((_, j) => j !== i));
  };

  // ── API call ──
  const callClaude = async (messages) => {
    const res = await fetch(ANTHROPIC_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: MODEL, max_tokens: 5000, system: SYSTEM_PROMPT, messages }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.content?.map((b) => b.text || "").join("") || "";
  };

  // Extract text from PDFs via local server, return images as-is
  const extractFileTexts = async () => {
    const results = { pdfTexts: [], imageContents: [] };
    for (const fc of fileContents) {
      if (fc.type === "application/pdf") {
        try {
          const res = await fetch(process.env.NODE_ENV === "production" ? "/api/parse-pdf" : "http://localhost:3001/api/parse-pdf", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ b64: fc.b64 }),
          });
          const { text } = await res.json();
          if (text) results.pdfTexts.push(`\n--- ${fc.name} ---\n${text.slice(0, 8000)}`);
        } catch {}
      } else if (fc.type.startsWith("image/")) {
        results.imageContents.push({ type: "image", source: { type: "base64", media_type: fc.type, data: fc.b64 } });
      }
    }
    return results;
  };

  // Build first message: images as base64, PDFs as extracted text (saves ~90% tokens)
  const buildFirstMessage = (textPrompt, pdfTexts = [], imageContents = []) => {
    const content = [...imageContents];
    if (pdfTexts.length > 0) {
      const pdfBlock = `CONTENU DES RELEVÉS BANCAIRES (texte extrait):\n${pdfTexts.join("\n")}`;
      content.push({ type: "text", text: pdfBlock });
    }
    content.push({ type: "text", text: textPrompt });
    return content;
  };

  // ── Analyze ──
  const analyze = async () => {
    if (!hasSomeData) return;
    setLoading(true);
    setAnalysis("");
    setScreen("result");
    setChatHistory([]);
    setExtractedMetrics(null);
    setBorrowingData(null);
    setMissingDocs([]);
    setTaxEstimate(null);
    setNewlyAddedByDoc({});

    const summaryLines = Object.entries(form)
      .filter(([, v]) => v && v.trim() !== "")
      .map(([k, v]) => `${LABELS[k]}: ${v}€`);
    const historyStr = monthlyHistory.length > 0
      ? monthlyHistory.slice(-3).map((m) => `${m.label}: revenus ${m.income}€, dépenses ${m.expenses}€`).join(" | ")
      : "";
    const textPrompt = ANALYSIS_PROMPT(
      summaryLines.join("\n") || "(aucune saisie — extraire depuis les documents)",
      files.map((f) => f.name).join(", "),
      historyStr,
      profile
    );

    try {
      // Extract PDF text locally first — avoids sending base64 to Claude (saves ~90% tokens)
      const { pdfTexts, imageContents } = await extractFileTexts();
      const firstContent = buildFirstMessage(textPrompt, pdfTexts, imageContents);
      const reply = await callClaude([{ role: "user", content: firstContent }]);

      // Parse JSON block
      const jsonMatch = reply.match(/```json\s*([\s\S]*?)```/);
      let extracted = null;
      if (jsonMatch) {
        try { extracted = JSON.parse(jsonMatch[1]); } catch {}
      }

      if (extracted) {
        // Multi-month extraction from bank statements
        if (extracted.months && extracted.months.length > 0) {
          const latestMonth = extracted.months[extracted.months.length - 1];
          setExtractedMetrics(latestMonth);

          const newEntries = extracted.months.map((m) => {
            const expenses = EXPENSE_KEYS.reduce((s, k) => s + (m[k] || 0), 0);
            return { month: m.month, label: m.label, income: m.income, expenses, balance: m.income - expenses };
          });
          const existing = monthlyHistory.filter((h) => !newEntries.find((ne) => ne.month === h.month));
          const updated = [...existing, ...newEntries].sort((a, b) => a.month.localeCompare(b.month)).slice(-12);
          setMonthlyHistory(updated);
          try { localStorage.setItem("budgetai_history", JSON.stringify(updated)); } catch {}
        } else {
          // Fallback: flat JSON (form data only)
          setExtractedMetrics(extracted);
          const now = new Date();
          const mk = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
          const income = parseFloat(form.income) || extracted.income || 0;
          const expenses = EXPENSE_KEYS.reduce((s, k) => s + (parseFloat(form[k]) || extracted[k] || 0), 0);
          const entry = { month: mk, label: `${MONTHS[now.getMonth()]} ${now.getFullYear()}`, income, expenses, balance: income - expenses };
          const updated = [...monthlyHistory.filter((m) => m.month !== mk), entry].slice(-12);
          setMonthlyHistory(updated);
          try { localStorage.setItem("budgetai_history", JSON.stringify(updated)); } catch {}
        }

        if (extracted.borrowing_capacity) setBorrowingData(extracted.borrowing_capacity);
        if (extracted.missing_documents?.length) setMissingDocs(extracted.missing_documents);
        if (extracted.tax_estimate) setTaxEstimate(extracted.tax_estimate);
      }

      const cleanReply = reply.replace(/```json[\s\S]*?```\s*/, "").trim();
      setAnalysis(cleanReply);
      setChatHistory([
        { role: "user", content: textPrompt },
        { role: "assistant", content: cleanReply },
      ]);
    } catch (e) {
      setAnalysis(`❌ Erreur: ${e.message}`);
    }
    setLoading(false);
  };

  // ── Analyze locally (no Claude) ──
  const analyzeLocal = async () => {
    if (!hasSomeData) return;
    setLoading(true);
    setAnalysis("");
    setScreen("result");
    setChatHistory([]);
    setExtractedMetrics(null);
    setBorrowingData(null);
    setMissingDocs([]);
    setTaxEstimate(null);
    setNewlyAddedByDoc({});
    setCustomDocs([]);

    let allMonths = [];
    const parseErrors = [];

    // Parse each PDF file via the server endpoint
    for (const fc of fileContents) {
      if (fc.type === "application/pdf") {
        try {
          const res = await fetch(process.env.NODE_ENV === "production" ? "/api/parse-pdf" : "http://localhost:3001/api/parse-pdf", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ b64: fc.b64 }),
          });
          const { text, error } = await res.json();
          if (error) throw new Error(error);
          const txs = parseTransactionsFromText(text);
          if (txs.length > 0) {
            const months = aggregateByMonth(txs);
            for (const m of months) {
              const existing = allMonths.find(e => e.month === m.month);
              if (existing) {
                ["income", ...EXPENSE_KEYS_LOCAL].forEach(k => { existing[k] = (existing[k] || 0) + (m[k] || 0); });
              } else {
                allMonths.push({ ...m });
              }
            }
          } else {
            parseErrors.push(`${fc.name} — aucune transaction détectée (relevé scanné ou format non reconnu)`);
          }
        } catch (e) {
          parseErrors.push(`${fc.name} — ${e.message}`);
        }
      } else if (fc.type.startsWith("image/")) {
        parseErrors.push(`${fc.name} — images non supportées en mode local (utilise le mode Claude)`);
      }
    }

    // Fallback to form data if no PDF data extracted
    if (allMonths.length === 0 && Object.values(form).some(v => v && parseFloat(v) > 0)) {
      const now = new Date();
      const mk = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,"0")}`;
      const m = { month: mk, label: `${MONTHS[now.getMonth()]} ${now.getFullYear()}`, income: parseFloat(form.income) || 0 };
      EXPENSE_KEYS.forEach(k => { m[k] = parseFloat(form[k]) || 0; });
      allMonths = [m];
    }

    if (allMonths.length === 0) {
      setAnalysis("❌ Impossible d'extraire des données. Vérifie que tes relevés sont des PDFs texte (non scannés) ou passe en mode Claude.");
      if (parseErrors.length) setMissingDocs(parseErrors);
      setLoading(false);
      return;
    }

    const { analysisText, metrics } = generateLocalAnalysis(allMonths, profile);
    setAnalysis(analysisText);
    setExtractedMetrics(metrics.latestMonth);
    setBorrowingData(metrics.borrowing_capacity);
    setTaxEstimate(metrics.tax_estimate);
    if (parseErrors.length) setMissingDocs(parseErrors);

    // Populate monthly history
    const newEntries = allMonths.map(m => {
      const expenses = EXPENSE_KEYS_LOCAL.reduce((s, k) => s + (m[k] || 0), 0);
      return { month: m.month, label: m.label, income: m.income, expenses, balance: m.income - expenses };
    });
    const existing = monthlyHistory.filter(h => !newEntries.find(ne => ne.month === h.month));
    const updated = [...existing, ...newEntries].sort((a, b) => a.month.localeCompare(b.month)).slice(-12);
    setMonthlyHistory(updated);
    try { localStorage.setItem("budgetai_history", JSON.stringify(updated)); } catch {}

    setChatHistory([
      { role: "user", content: "Analyse locale de mes finances" },
      { role: "assistant", content: analysisText },
    ]);
    setLoading(false);
  };

  // ── Chat ──
  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput("");

    const newHistory = [...chatHistory, { role: "user", content: msg }];
    setChatHistory(newHistory);
    setChatLoading(true);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

    try {
      // For follow-up questions, don't re-attach files (already in chat history context)
      const messages = newHistory.map((m) => ({ role: m.role, content: m.content }));
      const reply = await callClaude(messages);
      setChatHistory([...newHistory, { role: "assistant", content: reply }]);
    } catch (e) {
      setChatHistory([...newHistory, { role: "assistant", content: `❌ Erreur: ${e.message}` }]);
    }
    setChatLoading(false);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const reset = () => {
    setScreen("home");
    setAnalysis("");
    setChatHistory([]);
    setLoading(false);
    setExtractedMetrics(null);
    setBorrowingData(null);
    setMissingDocs([]);
    setTaxEstimate(null);
    setNewlyAddedByDoc({});
    setCustomDocs([]);
    setCustomDocLabel("");
    // Keep files in memory — user doesn't have to re-upload
  };

  // ── UI ──────────────────────────────────────────────────────────────────────

  const ed = expenseData();
  const sr = savingsRate();

  const pieData = ed.items.map((item) => ({
    name: item.label,
    value: item.value,
    pct: ed.income > 0 ? Math.round((item.value / ed.income) * 100) : 0,
  }));

  const lineData = monthlyHistory.map((m) => ({
    name: m.label,
    Revenus: m.income,
    Dépenses: m.expenses,
    Solde: m.balance,
  }));

  const quickQuestions = [
    "Quels placements concrets pour mon profil ?",
    "Comment optimiser ma déclaration d'impôts ?",
    "Détaille ma capacité d'emprunt immobilier",
    "Quelles dépenses réduire en priorité ?",
    "Plan d'épargne retraite adapté à ma situation",
  ];

  // ─ Global styles ─
  const s = {
    app: {
      minHeight: "100vh",
      background: "linear-gradient(160deg,#07090f 0%,#0a0f0a 100%)",
      fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif",
      color: "#e8eaed",
    },
    grid: {
      position: "fixed", inset: 0, pointerEvents: "none",
      backgroundImage: "linear-gradient(rgba(74,222,128,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(74,222,128,0.03) 1px,transparent 1px)",
      backgroundSize: "44px 44px",
    },
    header: {
      position: "sticky", top: 0, zIndex: 50,
      background: "rgba(7,9,15,0.94)", backdropFilter: "blur(16px)",
      borderBottom: "1px solid rgba(74,222,128,0.1)",
      padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    logo: {
      width: 32, height: 32, borderRadius: 8,
      background: "linear-gradient(135deg,#4ade80,#22d3ee)",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
    },
    container: { maxWidth: 640, margin: "0 auto", padding: "18px 15px 80px", position: "relative", zIndex: 1 },
    card: {
      background: "rgba(255,255,255,0.025)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 16, padding: "18px 20px", marginBottom: 14,
    },
    sectionTitle: {
      fontSize: 11, fontWeight: 700, color: "#6b7280",
      letterSpacing: 1.2, marginBottom: 14, textTransform: "uppercase",
    },
    fieldRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 9 },
    fieldLabel: { fontSize: 12.5, color: "#9ca3af", width: 185, flexShrink: 0 },
    fieldInput: {
      flex: 1, background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 8, padding: "8px 32px 8px 11px",
      color: "#e8eaed", fontSize: 14, outline: "none", boxSizing: "border-box",
    },
    btnPrimary: {
      width: "100%", padding: 15, borderRadius: 12, border: "none",
      background: hasSomeData ? "linear-gradient(135deg,#4ade80,#22d3ee)" : "rgba(74,222,128,0.12)",
      color: hasSomeData ? "#07090f" : "#4b5563",
      fontSize: 15, fontWeight: 800,
      cursor: hasSomeData ? "pointer" : "not-allowed",
      boxShadow: hasSomeData ? "0 6px 28px rgba(74,222,128,0.2)" : "none",
    },
    metric: {
      background: "rgba(255,255,255,0.04)", borderRadius: 10,
      padding: "12px 10px", textAlign: "center",
    },
    metricLabel: { fontSize: 11, color: "#6b7280", marginBottom: 4 },
    metricValue: { fontSize: 20, fontWeight: 700 },
    backBtn: {
      background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.2)",
      color: "#4ade80", padding: "6px 13px", borderRadius: 7,
      cursor: "pointer", fontSize: 12, fontWeight: 600,
    },
    select: {
      flex: 1, background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 8, padding: "8px 11px",
      color: "#e8eaed", fontSize: 13, outline: "none",
    },
    infoCard: {
      borderRadius: 12, padding: "14px 16px", marginBottom: 12,
      display: "flex", alignItems: "flex-start", gap: 10,
    },
  };

  return (
    <div style={s.app}>
      <div style={s.grid} />

      {/* Header */}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={s.logo}>💎</div>
          <span style={{ fontSize: 17, fontWeight: 800 }}>
            Budget<span style={{ color: "#4ade80" }}>AI</span>
          </span>
        </div>
        {screen !== "home" && (
          <button onClick={reset} style={s.backBtn}>← Nouveau mois</button>
        )}
      </div>

      <div style={s.container}>

        {/* ══════════════════════════════════════════════════════════ HOME */}
        {screen === "home" && (
          <>
            {/* Hero */}
            <div style={{ textAlign: "center", padding: "24px 0 22px" }}>
              <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.22)", fontSize: 10, color: "#4ade80", marginBottom: 14, letterSpacing: 1.5, fontWeight: 700 }}>
                POWERED BY CLAUDE AI
              </div>
              <h1 style={{ fontSize: "clamp(22px,5vw,34px)", fontWeight: 900, lineHeight: 1.2, margin: "0 0 10px", letterSpacing: "-0.8px" }}>
                Importe tes relevés.<br />
                <span style={{ color: "#4ade80" }}>Claude fait le reste.</span>
              </h1>
              <p style={{ color: "#6b7280", fontSize: 13, maxWidth: 360, margin: "0 auto", lineHeight: 1.6 }}>
                Télécharge tes relevés bancaires des derniers mois. BudgetAI extrait tout automatiquement — dépenses, épargne, impôts, capacité d'emprunt et conseils d'investissement.
              </p>
            </div>

            {/* Upload — primary action */}
            <div style={{ ...s.card, border: "2px dashed rgba(74,222,128,0.3)", textAlign: "center", padding: "22px 20px" }}>
              <div style={s.sectionTitle}>🏦 Relevés bancaires & documents</div>
              <p style={{ color: "#6b7280", fontSize: 12.5, marginBottom: 16, lineHeight: 1.6 }}>
                Relevés bancaires (tous tes mois) · Fiches de paie · Avis d'imposition<br />
                <span style={{ color: "#4b5563" }}>PDF et images · Plusieurs fichiers acceptés</span>
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                style={{ background: "linear-gradient(135deg,rgba(74,222,128,0.15),rgba(34,211,238,0.1))", border: "1px solid rgba(74,222,128,0.4)", color: "#4ade80", padding: "11px 28px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700 }}
              >
                + Importer des documents
              </button>
              {/* fileRef input is rendered globally below */}

              {files.length > 0 && (
                <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ background: "rgba(74,222,128,0.07)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 7, padding: "5px 11px", fontSize: 12, color: "#4ade80", display: "flex", alignItems: "center", gap: 6 }}>
                      📄 {f.name.length > 24 ? f.name.slice(0, 22) + "…" : f.name}
                      <span onClick={() => removeFile(i)} style={{ cursor: "pointer", color: "#f87171", fontWeight: 900 }}>×</span>
                    </div>
                  ))}
                </div>
              )}
              {files.length > 0 && (
                <p style={{ fontSize: 11.5, color: "#4ade80", marginTop: 10 }}>
                  ✓ {files.length} document(s) chargé(s) — Claude analysera chaque mois séparément
                </p>
              )}
            </div>

            {/* Profile */}
            <div style={s.card}>
              <div style={s.sectionTitle}>👤 Profil — optimisation fiscale & emprunt</div>
              {[
                {
                  label: "Situation familiale", key: "situation",
                  options: [["célibataire","Célibataire"],["marié","Marié(e)"],["pacsé","Pacsé(e)"],["divorcé","Divorcé(e) / Séparé(e)"]],
                },
                {
                  label: "Enfants à charge", key: "nb_enfants",
                  options: [["0","Aucun"],["1","1 enfant"],["2","2 enfants"],["3","3 enfants"],["4","4 enfants ou plus"]],
                },
                {
                  label: "Statut professionnel", key: "statut_pro",
                  options: [["salarié","Salarié(e)"],["indépendant","Indépendant / Freelance"],["fonctionnaire","Fonctionnaire"],["chef_entreprise","Chef d'entreprise"],["retraité","Retraité(e)"]],
                },
                {
                  label: "Résidence principale", key: "proprietaire",
                  options: [["locataire","Locataire"],["propriétaire","Propriétaire (sans emprunt)"],["emprunt_immobilier","En cours de remboursement"]],
                },
              ].map(({ label, key, options }) => (
                <div key={key} style={s.fieldRow}>
                  <label style={s.fieldLabel}>{label}</label>
                  <select
                    value={profile[key]}
                    onChange={(e) => setProfile((p) => ({ ...p, [key]: e.target.value }))}
                    style={s.select}
                  >
                    {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {/* Optional manual form (collapsible) */}
            <details style={{ ...s.card, cursor: "pointer" }}>
              <summary style={{ ...s.sectionTitle, marginBottom: 0, cursor: "pointer", userSelect: "none" }}>
                📝 Saisie manuelle (optionnel — si pas de relevés)
              </summary>
              <div style={{ marginTop: 14 }}>
                {Object.entries(LABELS).map(([k, lbl]) => (
                  <div key={k} style={s.fieldRow}>
                    <label style={{ ...s.fieldLabel, color: k === "income" ? "#4ade80" : "#9ca3af" }}>
                      {k === "income" ? "💰 " : ""}{lbl}
                    </label>
                    <div style={{ position: "relative", flex: 1 }}>
                      <input
                        type="number" min="0" placeholder="0"
                        value={form[k]}
                        onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                        style={{ ...s.fieldInput, borderColor: k === "income" ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.08)" }}
                      />
                      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#4ade80", fontSize: 12, fontWeight: 700 }}>€</span>
                    </div>
                  </div>
                ))}
              </div>
            </details>

            {/* Mode toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {[
                { id: false, icon: "🤖", label: "Analyse IA", sub: "Claude extrait & conseille" },
                { id: true,  icon: "⚡", label: "Mode local",  sub: "Sans API, instantané" },
              ].map(({ id, icon, label, sub }) => (
                <button
                  key={String(id)}
                  onClick={() => setLocalMode(id)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 10, border: "none", cursor: "pointer",
                    background: localMode === id ? (id ? "rgba(34,211,238,0.15)" : "rgba(74,222,128,0.15)") : "rgba(255,255,255,0.03)",
                    outline: localMode === id ? `2px solid ${id ? "#22d3ee" : "#4ade80"}` : "2px solid transparent",
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ fontSize: 18 }}>{icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: localMode === id ? (id ? "#22d3ee" : "#4ade80") : "#6b7280", marginTop: 3 }}>{label}</div>
                  <div style={{ fontSize: 10, color: "#4b5563", marginTop: 2 }}>{sub}</div>
                </button>
              ))}
            </div>

            <button onClick={localMode ? analyzeLocal : analyze} disabled={!hasSomeData} style={s.btnPrimary}>
              {localMode ? "⚡ Analyser en local (sans tokens)" : "🚀 Analyser avec Claude"}
            </button>
            {!hasSomeData && (
              <p style={{ textAlign: "center", color: "#374151", fontSize: 12, marginTop: 8 }}>
                Importe au moins un document ou remplis le formulaire
              </p>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════ RESULT */}
        {screen === "result" && (
          <>
            {/* Metrics */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, marginBottom: 14 }}>
              {[
                { label: "Revenus", value: `+${fmt(ed.income)} €`, color: "#4ade80" },
                { label: "Dépenses", value: `-${fmt(ed.total)} €`, color: "#f87171" },
                { label: "Solde mensuel", value: `${ed.balance >= 0 ? "+" : ""}${fmt(ed.balance)} €`, color: ed.balance >= 0 ? "#4ade80" : "#f87171" },
                { label: "Taux d'épargne", value: `${sr}%`, color: sr >= 20 ? "#4ade80" : sr >= 10 ? "#f59e0b" : "#f87171" },
              ].map((m) => (
                <div key={m.label} style={s.metric}>
                  <div style={s.metricLabel}>{m.label}</div>
                  <div style={{ ...s.metricValue, color: m.color }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Recap table */}
            <div style={s.card}>
              <div style={s.sectionTitle}>📋 Tableau récapitulatif</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <tbody>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    <td style={{ padding: "7px 4px", color: "#9ca3af" }}>Revenus (entrées)</td>
                    <td style={{ padding: "7px 4px", textAlign: "right", color: "#4ade80", fontWeight: 700 }}>+{fmt(ed.income)} €</td>
                  </tr>
                  {ed.items.map((item) => (
                    <tr key={item.key} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "6px 4px 6px 16px", color: "#6b7280", fontSize: 12 }}>{item.label}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 12 }}>-{fmt(item.value)} €</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
                    <td style={{ padding: "9px 4px", fontWeight: 700 }}>Solde mensuel</td>
                    <td style={{ padding: "9px 4px", textAlign: "right", fontWeight: 700, color: ed.balance >= 0 ? "#4ade80" : "#f87171" }}>
                      {ed.balance >= 0 ? "+" : ""}{fmt(ed.balance)} €
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Pie chart */}
            {pieData.length > 0 && (
              <div style={s.card}>
                <div style={s.sectionTitle}>🥧 Répartition des dépenses (% du revenu)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                  {pieData.map((d, i) => (
                    <span key={d.name} style={{ fontSize: 11, color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length], display: "inline-block" }} />
                      {d.name} {d.pct}%
                    </span>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={95}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="transparent" />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#111", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 8, fontSize: 12 }}
                      formatter={(val, name, props) => [`${fmt(val)} € (${props.payload.pct}%)`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Line chart */}
            {lineData.length > 1 ? (
              <div style={s.card}>
                <div style={s.sectionTitle}>📈 Évolution mensuelle ({lineData.length} mois)</div>
                <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 11 }}>
                  {[["Revenus","#4ade80"],["Dépenses","#f87171"],["Solde","#22d3ee"]].map(([l,c]) => (
                    <span key={l} style={{ color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: c, display: "inline-block" }} />{l}
                    </span>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(v) => `${v}€`} />
                    <Tooltip
                      contentStyle={{ background: "#111", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => `${fmt(v)} €`}
                    />
                    <Line type="monotone" dataKey="Revenus" stroke="#4ade80" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="Dépenses" stroke="#f87171" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="Solde" stroke="#22d3ee" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ ...s.card, textAlign: "center" }}>
                <p style={{ fontSize: 12.5, color: "#4b5563", lineHeight: 1.6 }}>
                  📊 La courbe d'évolution apparaîtra dès que 2 mois de données sont détectés.<br />
                  <span style={{ color: "#374151" }}>Importe plusieurs relevés mensuels pour voir ta progression.</span>
                </p>
              </div>
            )}

            {/* Missing documents alert */}
            {missingDocs.length > 0 && (() => {
              const totalAdded = Object.values(newlyAddedByDoc).reduce((s, n) => s + n, 0);
              return (
                <div style={{ ...s.card, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.3)" }}>
                  <div style={{ ...s.sectionTitle, color: "#fbbf24" }}>📋 Documents recommandés pour compléter l'analyse</div>
                  <p style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 12 }}>
                    Ajoute ces documents pour affiner les calculs d'impôt, de capacité d'emprunt et d'investissement :
                  </p>

                  {missingDocs.map((d, i) => {
                    const addedCount = newlyAddedByDoc[d] || 0;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: addedCount > 0 ? "rgba(74,222,128,0.06)" : "rgba(251,191,36,0.04)", border: `1px solid ${addedCount > 0 ? "rgba(74,222,128,0.2)" : "rgba(251,191,36,0.12)"}` }}>
                        <span style={{ fontSize: 15 }}>📄</span>
                        <span style={{ flex: 1, fontSize: 13, color: addedCount > 0 ? "#4ade80" : "#fbbf24" }}>{d}</span>
                        {addedCount > 0 && (
                          <span style={{ fontSize: 11.5, color: "#4ade80", fontWeight: 600, whiteSpace: "nowrap" }}>
                            ✓ {addedCount} fichier{addedCount > 1 ? "s" : ""} ajouté{addedCount > 1 ? "s" : ""}
                          </span>
                        )}
                        <button
                          onClick={() => addDocForType(d)}
                          style={{ flexShrink: 0, background: addedCount > 0 ? "rgba(74,222,128,0.1)" : "rgba(251,191,36,0.12)", border: `1px solid ${addedCount > 0 ? "rgba(74,222,128,0.3)" : "rgba(251,191,36,0.3)"}`, color: addedCount > 0 ? "#4ade80" : "#fbbf24", padding: "5px 12px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                        >
                          {addedCount > 0 ? "+ En ajouter" : "+ Ajouter"}
                        </button>
                      </div>
                    );
                  })}

                  {/* Free / unlisted document add */}
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(251,191,36,0.12)" }}>
                    <p style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 8 }}>Ajouter un autre document (non listé ci-dessus) :</p>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        value={customDocLabel}
                        onChange={e => setCustomDocLabel(e.target.value)}
                        placeholder="Intitulé du document…"
                        style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "7px 11px", color: "#e8eaed", fontSize: 12.5, outline: "none" }}
                      />
                      <button
                        disabled={!customDocLabel.trim()}
                        onClick={() => { if (customDocLabel.trim()) addDocForType(customDocLabel.trim()); }}
                        style={{ flexShrink: 0, background: customDocLabel.trim() ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", color: customDocLabel.trim() ? "#e8eaed" : "#4b5563", padding: "7px 14px", borderRadius: 8, cursor: customDocLabel.trim() ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700 }}
                      >
                        + Ajouter
                      </button>
                    </div>

                    {/* Show custom docs added */}
                    {Object.entries(newlyAddedByDoc)
                      .filter(([label]) => !missingDocs.includes(label))
                      .map(([label, count]) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, padding: "7px 10px", borderRadius: 8, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.18)" }}>
                          <span style={{ fontSize: 14 }}>📄</span>
                          <span style={{ flex: 1, fontSize: 12.5, color: "#4ade80" }}>{label}</span>
                          <span style={{ fontSize: 11.5, color: "#4ade80", fontWeight: 600 }}>✓ {count} fichier{count > 1 ? "s" : ""}</span>
                          <button
                            onClick={() => { setCustomDocLabel(label); addDocForType(label); }}
                            style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)", color: "#4ade80", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                          >
                            + En ajouter
                          </button>
                        </div>
                      ))}
                  </div>

                  {totalAdded > 0 && (
                    <button
                      onClick={localMode ? analyzeLocal : analyze}
                      style={{ width: "100%", marginTop: 12, padding: "11px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#4ade80,#22d3ee)", color: "#07090f", fontSize: 14, fontWeight: 800, cursor: "pointer" }}
                    >
                      🔄 Relancer l'analyse avec {totalAdded} nouveau{totalAdded > 1 ? "x" : ""} document{totalAdded > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Borrowing capacity + tax estimate side by side */}
            {(borrowingData || taxEstimate) && (
              <div style={{ display: "grid", gridTemplateColumns: borrowingData && taxEstimate ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 14 }}>
                {borrowingData && (() => {
                  // Support both old flat format and new detailed format
                  const existingLoans  = borrowingData.existing_loan_payments ?? 0;
                  const maxRatio       = borrowingData.max_debt_ratio_payment  ?? borrowingData.max_monthly_payment ?? 0;
                  const remaining      = borrowingData.remaining_capacity       ?? borrowingData.max_monthly_payment ?? 0;
                  const newLoanAmt     = borrowingData.max_new_loan_amount      ?? borrowingData.max_loan_amount     ?? 0;
                  const atCap          = remaining <= 0;
                  return (
                    <div style={{ ...s.card, marginBottom: 0, background: atCap ? "rgba(248,113,113,0.05)" : "rgba(34,211,238,0.04)", border: `1px solid ${atCap ? "rgba(248,113,113,0.25)" : "rgba(34,211,238,0.15)"}` }}>
                      <div style={{ ...s.sectionTitle, color: atCap ? "#f87171" : "#22d3ee" }}>🏦 Capacité d'emprunt résiduelle</div>

                      {/* Main number */}
                      <div style={{ fontSize: 22, fontWeight: 800, color: atCap ? "#f87171" : "#22d3ee", marginBottom: 2 }}>
                        {atCap ? "Plafond atteint" : `${fmt(newLoanAmt)} €`}
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
                        {atCap ? "endettement déjà au maximum légal" : "montant supplémentaire empruntable"}
                      </div>

                      {/* Breakdown */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#9ca3af" }}>
                          <span>Plafond légal 35%</span>
                          <strong style={{ color: "#e8eaed" }}>{fmt(maxRatio)} €/mois</strong>
                        </div>
                        {existingLoans > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#9ca3af" }}>
                            <span>Crédits en cours</span>
                            <strong style={{ color: "#f87171" }}>− {fmt(existingLoans)} €/mois</strong>
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 5, borderTop: "1px solid rgba(255,255,255,0.07)", color: "#9ca3af" }}>
                          <span>Capacité résiduelle</span>
                          <strong style={{ color: atCap ? "#f87171" : "#22d3ee" }}>{atCap ? "0" : fmt(remaining)} €/mois</strong>
                        </div>
                      </div>

                      <div style={{ fontSize: 11, color: "#4b5563", marginTop: 8 }}>
                        {borrowingData.rate_pct}% · {borrowingData.duration_years} ans
                      </div>
                    </div>
                  );
                })()}
                {taxEstimate && (
                  <div style={{ ...s.card, marginBottom: 0, background: taxEstimate.alerte_erreur ? "rgba(248,113,113,0.05)" : "rgba(167,139,250,0.04)", border: `1px solid ${taxEstimate.alerte_erreur ? "rgba(248,113,113,0.25)" : "rgba(167,139,250,0.15)"}` }}>
                    <div style={{ ...s.sectionTitle, color: taxEstimate.alerte_erreur ? "#f87171" : "#a78bfa" }}>
                      📑 Impôt estimé {new Date().getFullYear()}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: taxEstimate.alerte_erreur ? "#f87171" : "#a78bfa", marginBottom: 4 }}>{fmt(taxEstimate.impot_estime)} €</div>
                    <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 8 }}>sur {fmt(taxEstimate.revenu_annuel_estime)} € de revenu</div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>Taux effectif <strong style={{ color: "#e8eaed" }}>{taxEstimate.taux_effectif_pct}%</strong></div>
                    <div style={{ fontSize: 11, color: "#4b5563", marginTop: 3 }}>{taxEstimate.nb_parts} part(s) fiscale(s)</div>
                    {taxEstimate.alerte_erreur && (
                      <div style={{ fontSize: 11.5, color: "#f87171", marginTop: 8, fontWeight: 600 }}>⚠️ Écart possible — vérifier</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* AI Analysis */}
            <div style={{ ...s.card, borderColor: "rgba(74,222,128,0.15)" }}>
              <div style={{ ...s.sectionTitle, color: "#4ade80" }}>✨ Analyse financière complète — Claude AI</div>
              {loading ? (
                <div style={{ textAlign: "center", padding: "28px 0" }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>Claude analyse tes finances…</p>
                </div>
              ) : (
                <MarkdownRenderer text={analysis} />
              )}
              {files.length > 0 && (
                <div style={{ fontSize: 11.5, color: "#4ade80", marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(74,222,128,0.12)" }}>
                  📎 {files.length} document(s) en mémoire — réinjectés automatiquement dans chaque question de suivi
                </div>
              )}
            </div>

            {/* Chat */}
            {!loading && (
              <div style={s.card}>
                <div style={s.sectionTitle}>💬 Questions de suivi</div>

                {/* Messages */}
                {chatHistory.length > 2 && (
                  <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    {chatHistory.slice(2).map((m, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                        <div style={{
                          maxWidth: "88%", padding: "10px 14px", borderRadius: 14, fontSize: 13, lineHeight: 1.6,
                          background: m.role === "user" ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${m.role === "user" ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.08)"}`,
                        }}>
                          {m.role === "assistant"
                            ? <MarkdownRenderer text={m.content} />
                            : <span style={{ color: "#d1fae5" }}>{m.content}</span>
                          }
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div style={{ display: "flex" }}>
                        <div style={{ padding: "10px 14px", borderRadius: 14, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#4ade80", fontSize: 13 }}>
                          ⏳ BudgetAI réfléchit…
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}

                {/* Quick questions */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      onClick={() => setChatInput(q)}
                      style={{ background: "rgba(74,222,128,0.07)", border: "1px solid rgba(74,222,128,0.18)", color: "#4ade80", padding: "6px 11px", borderRadius: 18, cursor: "pointer", fontSize: 11.5, fontWeight: 500 }}
                    >
                      {q}
                    </button>
                  ))}
                </div>

                {/* Input */}
                <div style={{ display: "flex", gap: 9, background: "rgba(7,9,15,0.6)", padding: 10, borderRadius: 12, border: "1px solid rgba(74,222,128,0.12)" }}>
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendChat()}
                    placeholder="Pose une question à BudgetAI…"
                    style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 9, padding: "10px 13px", color: "#e8eaed", fontSize: 13.5, outline: "none" }}
                  />
                  <button
                    onClick={sendChat}
                    disabled={chatLoading || !chatInput.trim()}
                    style={{
                      background: chatInput.trim() ? "linear-gradient(135deg,#4ade80,#22d3ee)" : "rgba(74,222,128,0.12)",
                      border: "none", borderRadius: 9, padding: "10px 16px",
                      cursor: chatInput.trim() ? "pointer" : "not-allowed",
                      fontSize: 17, color: chatInput.trim() ? "#07090f" : "#4ade80", fontWeight: 900,
                    }}
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Global hidden file input — accessible from any screen */}
      <input ref={fileRef} type="file" multiple accept=".pdf,image/*" onChange={(e) => handleFiles(e.target.files)} style={{ display: "none" }} />
    </div>
  );
}
